import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { getApiUrl } from '@/utils/apiUrl';

const STATUS_LABELS = { nouveau: 'Nouveau', ouvert: 'Ouvert', en_cours: 'En cours', en_traitement: 'En traitement' };

export const FormsTrackingWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const API = getApiUrl();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/forms-tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) { void 0; }
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data || data.total === 0) return null;
  const shown = expanded ? data.files : data.files.slice(0, 5);

  return (
    <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-50 to-yellow-50" data-testid="forms-tracking-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600">
              <ClipboardList className="h-5 w-5 text-white" />
            </div>
            Formulaires à compléter avant soumission
          </CardTitle>
          <Badge className="bg-amber-600 text-white" data-testid="forms-tracking-count">
            {data.total} dossier{data.total > 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {shown.map(f => (
          <div key={f.id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-200 px-3 py-2"
            data-testid={`forms-tracking-file-${f.file_number}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-sm text-blue-700">{f.file_number}</span>
                <span className="text-sm text-gray-700 truncate">{f.client_name}</span>
                <Badge variant="outline" className="text-[9px]">{STATUS_LABELS[f.status] || f.status}</Badge>
              </div>
              <div className="flex items-center gap-1 flex-wrap mt-1">
                {f.missing_forms.map((code, i) => (
                  <Badge key={i} variant="outline" className="text-[9px] font-mono border-amber-300 text-amber-700 bg-amber-50">
                    {code.length > 30 ? code.slice(0, 28) + '…' : code}
                  </Badge>
                ))}
                {f.missing_count > f.missing_forms.length && (
                  <span className="text-[10px] text-gray-400">+{f.missing_count - f.missing_forms.length}</span>
                )}
              </div>
            </div>
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 shrink-0"
              onClick={() => navigate(`/files/${f.id}`)} data-testid={`forms-tracking-open-${f.file_number}`}>
              Ouvrir
            </Button>
          </div>
        ))}
        {data.files.length > 5 && (
          <button onClick={() => setExpanded(!expanded)}
            className="w-full text-center text-xs font-semibold text-amber-700 hover:text-amber-900 py-1 flex items-center justify-center gap-1"
            data-testid="forms-tracking-toggle">
            {expanded ? <>Réduire <ChevronUp className="h-3 w-3" /></> : <>Voir les {data.files.length - 5} autres <ChevronDown className="h-3 w-3" /></>}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

export default FormsTrackingWidget;
